import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import './Map.css';
import landmarksData from '../../landmarks.json';
import { getMemoriesForViewport } from '../../utils/supabase';
import { registerStickerSprites } from '../../utils/stickerSprites';
import { getAllLandmarkZones, getZoneStyling } from '../../utils/landmarkZones';
import { logger } from '../../utils/logger';

// Default color palette (fallback)
export const DEFAULT_COLORS = {
  background: '#FAF7F2',
  ocean: '#a8d4e6',
  land: '#f5f0e8',
  parks: '#d4e6c8',
  buildings: '#e8e0d5',
  roads: '#d4cfc6',
  borders: '#c5bbb0',
  labels: '#5a5a5a',
  romantic: '#E6B3C2',
  gold: '#F2C6A0',
  legendary: '#B8A0C4',
};

export const PHRASES = {
  1: [
    "Nothing to see here. Move along.",
    "Deserted. Even the tumbleweeds left.",
    "We looked. It looked back. It was empty.",
    "Zero. Zilch. Nada.",
    "Not even a suspicious breadcrumb.",
    "The void says hi.",
    "Silence… dramatic silence.",
    "This page is on a juice cleanse.",
    "We found absolutely vibes.",
    "Nothing to see here creep!",
    "It’s giving… nothing.",
    "Even Dora gave up.",
    "Mission failed successfully.",
    "404 emotions detected.",
    "Not a pixel in sight.",
    "This space available for rent.",
    "Nothing to sniff here, soldier.",
    "It’s just you and your thoughts now.",
    "The algorithm shrugged.",
    "Fresh air. Premium emptiness.",
    "Be the first to make history."],
  0: [
    "Oh we’re cooking now. 👀",
    "A wild result appeared.",
    "Plot twist unlocked.",
    "This might be your villain origin story.",
    "Something just entered the chat.",
    "Curiosity has been summoned.",
    "You might want to click that…",
    "The breadcrumbs are breadcrumbing.",
    "This looks promising. Suspiciously promising.",
    "New lore just dropped.",
    "It’s giving opportunity.",
    "Go on… tap it.",
    "The universe nudged you.",
    "This could get interesting.",
    "We found treasure (probably).",
    "Exploration mode: activated.",
    "A connection is forming…",
    "Destiny just pinged you.",
    "This might be your villain origin story.",
    "This one has main character energy.",
    "Proceed with confidence."
  ]
}

// Export COLORS for backward compatibility
export const COLORS = DEFAULT_COLORS;

// Transform landmarks data
const LANDMARKS = landmarksData.landmarks.map(l => ({
  id: l.id, name: l.name, lat: l.lat, lng: l.lng, icon: l.emoji,
  category: l.category, rarity: l.rarity, city: l.city,
  country: l.country, description: l.description, yearBuilt: l.yearBuilt
}));

// OpenFreeMap base style
const BASE_STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';

// Empty GeoJSON for initial state
const EMPTY_GEOJSON = {
  type: 'FeatureCollection',
  features: []
};

// Minimum zoom level to fetch and display memories
const MIN_ZOOM_FOR_DATA = 8;

// TODO: IS_DEBUG_MODE bypasses ALL zoom restrictions on localhost, meaning memories
// are fetched from zoom 0+. Re-enable zoom restrictions (or at minimum, only bypass
// for zoom 12+ where we want to fetch all memories). Remove or scope this flag.
// Debug mode: bypass zoom restrictions on localhost
const IS_DEBUG_MODE = window.location.hostname === 'test' || window.location.hostname === '127.0.0.1';
if (IS_DEBUG_MODE) {
  logger.debug('🔧 Debug mode active: zoom restrictions bypassed, all memories visible');
}

// World view for share links (start before flyTo memory, same feel as landmark flow)
const WORLD_VIEW = { center: [10, 30], zoom: 2, pitch: 0, bearing: 0 };
const DEFAULT_VIEW = { center: [2.35, 48.85], zoom: 4, pitch: 20 };

// Landmark-style flyTo (same transition as clicking a landmark)
const FLY_TO_MEMORY = { zoom: 17, pitch: 60, duration: 2000 };

/**
 * Create a circle polygon approximation for rendering zones on the map
 * @param {number} centerLng - Center longitude
 * @param {number} centerLat - Center latitude
 * @param {number} radiusKm - Radius in kilometers
 * @param {number} points - Number of points to approximate the circle (default 64)
 * @returns {Array<[number, number]>} Array of [lng, lat] coordinates
 */
function createCirclePolygon(centerLng, centerLat, radiusKm, points = 64) {
  const coordinates = [];
  const earthRadius = 6371; // km

  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * 2 * Math.PI;

    // Calculate offset in radians
    const latOffset = (radiusKm / earthRadius) * Math.cos(angle);
    const lngOffset = (radiusKm / earthRadius) * Math.sin(angle) / Math.cos(centerLat * Math.PI / 180);

    coordinates.push([
      centerLng + lngOffset * (180 / Math.PI),
      centerLat + latOffset * (180 / Math.PI),
    ]);
  }

  return coordinates;
}

export default function Map({ onLocationClick, newMemory, onNewMemoryDisplayed, showMyMemories, user, theme, initialView = 'default', flyToSharedMemory, onSharedMemoryFlyComplete, selectedLocation, showLocationCard }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({ landmarks: [], newMemory: null });
  const [isLoaded, setIsLoaded] = useState(false);
  const [memoriesInView, setMemoriesInView] = useState(0);
  const [currentZoom, setCurrentZoom] = useState(4);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const fetchTimeoutRef = useRef(null);
  const lastFetchRef = useRef({ bounds: null });
  const currentGeoJsonRef = useRef(EMPTY_GEOJSON);
  const fetchMemoriesRef = useRef(null); // Ref to always call latest fetchMemories
  const onLocationClickRef = useRef(onLocationClick);
  const flownSharedMemoryIdRef = useRef(null);
  const [discovered, setDiscovered] = useState(() => {
    try { return JSON.parse(localStorage.getItem('discoveredPlaces') || '[]'); }
    catch { return []; }
  });
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(() => {
    return localStorage.getItem('hasSeenMapOnboarding') === 'true';
  });
  const [showOnboardingTrigger, setShowOnboardingTrigger] = useState(false);
  const [statePhrase, setStatePhrase] = useState(null);

  // Get colors from theme or use defaults (reactive to theme changes)
  const colors = useMemo(() => theme?.colors || DEFAULT_COLORS, [theme]);
  // Build landmark GeoJSON (GPU-rendered symbol layer instead of 33 DOM elements)
  const buildLandmarkGeoJSON = useCallback((discoveredList) => ({
    type: 'FeatureCollection',
    features: LANDMARKS.map(l => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [l.lng, l.lat] },
      properties: {
        id: l.id, name: l.name, icon: l.icon, rarity: l.rarity,
        category: l.category, city: l.city, country: l.country,
        description: l.description, yearBuilt: l.yearBuilt,
        lat: l.lat, lng: l.lng,
        discovered: discoveredList.includes(l.name) ? 1 : 0,
      },
    })),
  }), []);

  // Add landmarks as a symbol layer
  const addLandmarks = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    // Get available font from the style
    const style = map.getStyle();
    const existingTextLayer = style?.layers?.find(l => l.layout && l.layout['text-font']);
    const availableFont = existingTextLayer?.layout?.['text-font']?.[0] || 'Noto Sans Regular';

    map.addSource('landmarks', {
      type: 'geojson',
      data: buildLandmarkGeoJSON(discovered),
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 55,
      clusterMinPoints: 2,
    });

    const unclusteredLandmark = ['!', ['has', 'point_count']];

    // Grouped landmarks when zoomed out (same mechanism as memory-clusters)
    map.addLayer({
      id: 'landmark-clusters',
      type: 'circle',
      source: 'landmarks',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#F2C6A0',
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          18,
          10,
          24,
          30,
          30,
          50,
          36,
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#d8a48f',
        'circle-opacity': 0.92,
      },
    });

    map.addLayer({
      id: 'landmark-cluster-count',
      type: 'symbol',
      source: 'landmarks',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': [availableFont],
        'text-size': 13,
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': '#5a4634',
        'text-halo-color': 'rgba(255, 255, 255, 0.9)',
        'text-halo-width': 1.5,
      },
    });

    // Background circle for pin effect
    map.addLayer({
      id: 'landmark-bg',
      type: 'circle',
      source: 'landmarks',
      filter: unclusteredLandmark,
      paint: {
        'circle-radius': 20,
        'circle-color': [
          'match', ['get', 'rarity'],
          'legendary', '#ffd700',
          'epic', '#9370db',
          'rare', '#4169e1',
          '#f2c6a0'
        ],
        'circle-opacity': 0.85,
        'circle-stroke-width': 2,
        'circle-stroke-color': [
          'match', ['get', 'rarity'],
          'legendary', '#daa520',
          'epic', '#8a2be2',
          'rare', '#4169e1',
          '#d8a48f'
        ],
      },
    });

    // Discovered checkmark layer
    map.addLayer({
      id: 'landmark-discovered',
      type: 'symbol',
      source: 'landmarks',
      filter: ['all', unclusteredLandmark, ['==', ['get', 'discovered'], 1]],
      layout: {
        'text-field': '✓',
        'text-size': 10,
        'text-offset': [1.2, -1.2],
        'text-allow-overlap': true,
        'text-font': [availableFont],
      },
      paint: {
        'text-color': '#fff',
        'text-halo-color': '#5a9367',
        'text-halo-width': 5,
      },
    });

    // Register emoji sprites for each unique landmark icon
    const uniqueEmojis = [...new Set(LANDMARKS.map(l => l.icon))];
    uniqueEmojis.forEach(emoji => {
      const spriteName = `landmark-emoji-${emoji}`;
      if (!map.hasImage(spriteName)) {
        const size = 48;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.font = `${size * 0.7}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(emoji, size / 2, size / 2);
        const imageData = ctx.getImageData(0, 0, size, size);
        map.addImage(spriteName, { width: size, height: size, data: imageData.data });
      }
    });

    // Emoji icon layer (using canvas-rendered sprites)
    map.addLayer({
      id: 'landmark-icons',
      type: 'symbol',
      source: 'landmarks',
      filter: unclusteredLandmark,
      layout: {
        'icon-image': ['concat', 'landmark-emoji-', ['get', 'icon']],
        'icon-size': 0.55,
        'icon-allow-overlap': true,
      },
    });

    // Name label (visible on hover/high zoom)
    map.addLayer({
      id: 'landmark-labels',
      type: 'symbol',
      source: 'landmarks',
      filter: unclusteredLandmark,
      minzoom: 8,
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 11,
        'text-font': [availableFont],
        'text-offset': [0, 2.2],
        'text-anchor': 'top',
      },
      paint: {
        'text-color': '#5a5a5a',
        'text-halo-color': 'rgba(255,255,255,0.95)',
        'text-halo-width': 2,
      },
    });

    // Click handler
    map.on('click', 'landmark-icons', (e) => {
      if (!e.features?.length) return;
      const props = e.features[0].properties;
      const landmark = LANDMARKS.find(l => l.id === props.id);
      if (!landmark) return;

      const isNewDiscovery = !discovered.includes(landmark.name);
      if (isNewDiscovery) {
        const newDisc = [...discovered, landmark.name];
        setDiscovered(newDisc);
        localStorage.setItem('discoveredPlaces', JSON.stringify(newDisc));
      }
      onLocationClickRef.current?.({ ...landmark, isLandmark: true, isNewDiscovery });
      map.flyTo({ center: [landmark.lng, landmark.lat], zoom: 17, pitch: 60, duration: 2000 });
    });
    // Also handle click on the background circle
    map.on('click', 'landmark-bg', (e) => {
      if (!e.features?.length) return;
      const props = e.features[0].properties;
      const landmark = LANDMARKS.find(l => l.id === props.id);
      if (!landmark) return;

      const isNewDiscovery = !discovered.includes(landmark.name);
      if (isNewDiscovery) {
        const newDisc = [...discovered, landmark.name];
        setDiscovered(newDisc);
        localStorage.setItem('discoveredPlaces', JSON.stringify(newDisc));
      }
      onLocationClickRef.current?.({ ...landmark, isLandmark: true, isNewDiscovery });
      map.flyTo({ center: [landmark.lng, landmark.lat], zoom: 17, pitch: 60, duration: 2000 });
    });

    const expandLandmarkCluster = (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['landmark-clusters', 'landmark-cluster-count'],
      });
      if (!features.length) return;
      const clusterId = features[0].properties.cluster_id;
      const source = map.getSource('landmarks');
      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        map.easeTo({
          center: features[0].geometry.coordinates,
          zoom: zoom + 0.5,
          duration: 500,
        });
      });
    };
    map.on('click', 'landmark-clusters', expandLandmarkCluster);
    map.on('click', 'landmark-cluster-count', expandLandmarkCluster);

    map.on('mouseenter', 'landmark-clusters', () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'landmark-clusters', () => { map.getCanvas().style.cursor = ''; });
    map.on('mouseenter', 'landmark-cluster-count', () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'landmark-cluster-count', () => { map.getCanvas().style.cursor = ''; });

    map.on('mouseenter', 'landmark-icons', () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'landmark-icons', () => { map.getCanvas().style.cursor = ''; });
    map.on('mouseenter', 'landmark-bg', () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'landmark-bg', () => { map.getCanvas().style.cursor = ''; });
  }, [discovered, buildLandmarkGeoJSON]);

  // Fetch memories for current viewport
  const fetchMemories = useCallback(async () => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const zoom = Math.floor(map.getZoom());

    // Don't fetch at low zoom - unless:
    // 1. Showing only user's memories (bypass zoom restriction)
    // 2. Debug mode on localhost (bypass zoom restriction for testing)
    const isMyMemoriesMode = showMyMemories && user?.uid;
    if (zoom < MIN_ZOOM_FOR_DATA && !isMyMemoriesMode && !IS_DEBUG_MODE) {
      const source = map.getSource('memories');
      if (source) {
        source.setData(EMPTY_GEOJSON);
      }
      setMemoriesInView(0);
      return;
    }

    const bounds = map.getBounds();
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();

    // Debounce: skip if bounds haven't changed significantly
    if (lastFetchRef.current.bounds) {
      const last = lastFetchRef.current.bounds;
      const latChange = Math.abs(sw.lat - last.sw.lat) + Math.abs(ne.lat - last.ne.lat);
      const lngChange = Math.abs(sw.lng - last.sw.lng) + Math.abs(ne.lng - last.ne.lng);
      if (latChange < 0.01 && lngChange < 0.01) {
        return;
      }
    }

    // Expand viewport by 20% to prefetch nearby data and reduce re-fetches on small pans
    const latPadding = (ne.lat - sw.lat) * 0.1;
    const lngPadding = (ne.lng - sw.lng) * 0.1;
    const boundsObj = {
      sw: { lat: sw.lat - latPadding, lng: sw.lng - lngPadding },
      ne: { lat: ne.lat + latPadding, lng: ne.lng + lngPadding }
    };

    lastFetchRef.current = { bounds: boundsObj };

    const geojson = await getMemoriesForViewport(boundsObj);

    if (!geojson || !geojson.features) {
      logger.error('Invalid GeoJSON response:', geojson);
      return;
    }

    // Client-side filtering for "show my memories" toggle
    const filteredFeatures = showMyMemories && user?.uid
      ? geojson.features.filter(f => f.properties.user_id === user.uid)
      : geojson.features;

    logger.debug(`📍 Zoom ${zoom}: ${filteredFeatures.length} memories (${geojson.features.length} total)`);

    const source = map.getSource('memories');
    if (source) {
      const data = {
        type: 'FeatureCollection',
        features: filteredFeatures
      };
      currentGeoJsonRef.current = data;
      source.setData(data);
      setMemoriesInView(geojson.features?.length || 0);
    } else {
      setTimeout(() => fetchMemories(), 500);
    }
  }, [showMyMemories, user]);

  // Keep refs updated with latest callbacks
  useEffect(() => {
    fetchMemoriesRef.current = fetchMemories;
  }, [fetchMemories]);
  useEffect(() => {
    onLocationClickRef.current = onLocationClick;
  }, [onLocationClick]);

  // Debounced fetch - uses ref to always call latest version
  const debouncedFetch = useCallback(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    fetchTimeoutRef.current = setTimeout(() => {
      fetchMemoriesRef.current?.();
    }, 800);
  }, []); // No dependencies - stable reference

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    let isMounted = true;
    let mapForCleanup = null;
    const markersForCleanup = markersRef.current;
    const abortController = new AbortController();

    fetch(BASE_STYLE_URL, { signal: abortController.signal })
      .then(res => res.json())
      .then(style => {
        if (!isMounted || mapRef.current || !containerRef.current) return;

        // Remove natural_earth source and layers
        delete style.sources.natural_earth;
        style.layers = style.layers.filter(l => l.source !== 'natural_earth');

        // Remove POI (Point of Interest) layers - bus stations, parking, toilets, etc.
        const poiKeywords = ['poi', 'amenity', 'transport', 'parking', 'toilet', 'bus', 'station', 'stop', 'shop', 'restaurant', 'cafe', 'fuel', 'atm', 'bank', 'pharmacy', 'hospital', 'school', 'university'];
        style.layers = style.layers.filter(layer => {
          const layerId = layer.id?.toLowerCase() || '';
          return !poiKeywords.some(keyword => layerId.includes(keyword));
        });

        // Customize colors using theme
        const themeColors = theme?.colors || DEFAULT_COLORS;
        style.layers.forEach(layer => {
          if (!layer.paint) return;

          // Background
          if (layer.id === 'background') {
            layer.paint['background-color'] = themeColors.land;
          }

          // Water layers
          if (layer.id.includes('water') && layer.type === 'fill') {
            layer.paint['fill-color'] = themeColors.ocean;
          }

          // Parks and green areas (forest, park, nature)
          if ((layer.id.includes('park') || layer.id.includes('forest') ||
            layer.id.includes('nature') || layer.id.includes('landcover')) &&
            layer.type === 'fill' && layer.paint['fill-color']) {
            layer.paint['fill-color'] = themeColors.parks;
          }

          // Roads
          if (layer.id.includes('road') && layer.type === 'line' && layer.paint['line-color']) {
            layer.paint['line-color'] = themeColors.roads;
          }

          // Buildings (2D fill layers)
          if (layer.id.includes('building') && layer.type === 'fill' &&
            !layer.id.includes('3d') && layer.paint['fill-color']) {
            layer.paint['fill-color'] = themeColors.buildings;
          }

          // Borders
          if (layer.id.includes('border') && layer.type === 'line' && layer.paint['line-color']) {
            layer.paint['line-color'] = themeColors.borders;
          }
        });

        const view = initialView === 'world' ? WORLD_VIEW : DEFAULT_VIEW;
        // Cap pixel ratio to 2x on mobile — 3x is ~55% more GPU work with negligible visual gain on small screens
        const isMobile = window.innerWidth < 768;
        const map = new maplibregl.Map({
          container: containerRef.current,
          style: style,
          center: view.center,
          zoom: view.zoom,
          minZoom: 2,
          maxZoom: 18,
          pitch: view.pitch ?? DEFAULT_VIEW.pitch,
          bearing: view.bearing ?? 0,
          pixelRatio: isMobile ? Math.min(window.devicePixelRatio, 2) : window.devicePixelRatio,
          // Memory optimization: limit tile cache size to prevent unbounded growth
          maxTileCacheSize: 50,
          // Block natural_earth raster tiles that slip through style filtering
          transformRequest: (url) => {
            if (url.includes('natural_earth')) {
              return { url: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=' };
            }
            return { url };
          },
        });

        mapRef.current = map;
        mapForCleanup = map;

        map.on('load', () => {
          setIsLoaded(true);

          // Add 3D buildings
          try {
            if (!map.getLayer('buildings-3d')) {
              map.addLayer({
                id: 'buildings-3d',
                type: 'fill-extrusion',
                source: 'openmaptiles',
                'source-layer': 'building',
                minzoom: 15,
                paint: {
                  'fill-extrusion-color': [
                    'interpolate', ['linear'], ['coalesce', ['get', 'render_height'], 10],
                    0, colors.buildings, 30, colors.gold, 80, colors.romantic
                  ],
                  'fill-extrusion-height': ['coalesce', ['get', 'render_height'], 10],
                  'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
                  'fill-extrusion-opacity': 0.8
                }
              });
            }
          } catch (e) {
            logger.warn('Could not add 3D buildings:', e);
          }

          // Add landmark premium zones (visible at zoom 10+)
          try {
            const zones = getAllLandmarkZones();

            // Create GeoJSON features for each zone (as circles approximated by polygons)
            const zoneFeatures = zones.map(zone => {
              const styling = getZoneStyling(zone.landmark.rarity);
              // Create circle polygon (approximation with 64 points)
              const coordinates = createCirclePolygon(
                zone.landmark.lng,
                zone.landmark.lat,
                zone.radiusKm
              );

              return {
                type: 'Feature',
                properties: {
                  id: zone.landmark.id,
                  rarity: zone.landmark.rarity,
                  fillColor: styling.fillColor,
                  borderColor: styling.borderColor,
                },
                geometry: {
                  type: 'Polygon',
                  coordinates: [coordinates],
                },
              };
            });

            map.addSource('landmark-zones', {
              type: 'geojson',
              data: {
                type: 'FeatureCollection',
                features: zoneFeatures,
              },
            });

            // Zone fill layer (semi-transparent)
            map.addLayer({
              id: 'landmark-zones-fill',
              type: 'fill',
              source: 'landmark-zones',
              minzoom: 10,
              paint: {
                'fill-color': ['get', 'fillColor'],
                'fill-opacity': 1,
              },
            });

            // Zone border layer
            map.addLayer({
              id: 'landmark-zones-border',
              type: 'line',
              source: 'landmark-zones',
              minzoom: 10,
              paint: {
                'line-color': ['get', 'borderColor'],
                'line-width': 2,
                'line-opacity': 1,
              },
            });

            logger.debug('✅ Landmark premium zones added');
          } catch (e) {
            logger.warn('Could not add landmark zones:', e);
          }

          // Add memories source with native clustering
          map.addSource('memories', {
            type: 'geojson',
            data: EMPTY_GEOJSON,
            cluster: true,
            clusterMaxZoom: 13,    // Stop clustering at zoom 13
            clusterRadius: 60,     // Cluster radius in pixels
            clusterMinPoints: 2,   // Min points to form a cluster
          });

          // Register sticker sprites
          registerStickerSprites(map);

          // Get available font from the style
          const existingTextLayer = style.layers.find(l => l.layout && l.layout['text-font']);
          const availableFont = existingTextLayer?.layout?.['text-font']?.[0] || 'Noto Sans Regular';
          logger.debug('📍 Using font:', availableFont);

          // ========== CLUSTER LAYER ==========
          // Shows clustered points with count
          map.addLayer({
            id: 'memory-clusters',
            type: 'symbol',
            source: 'memories',
            filter: ['has', 'point_count'],  // Only show clusters
            layout: {
              'icon-image': 'cluster-rose',
              'icon-size': [
                'interpolate', ['linear'], ['get', 'point_count'],
                2, 0.7,
                10, 0.85,
                50, 1.0,
                100, 1.15
              ],
              'icon-allow-overlap': true,
              'text-field': '{point_count_abbreviated}',
              'text-font': [availableFont],
              'text-size': 14,
              'text-anchor': 'center',
              'text-allow-overlap': true,
            },
            paint: {
              'text-color': '#4A3F35',
              'text-halo-color': 'rgba(255, 255, 255, 0.8)',
              'text-halo-width': 1.5,
            }
          });

          // ========== INDIVIDUAL NOTES LAYER ==========
          // Shows unclustered individual memories with text
          map.addLayer({
            id: 'memory-notes',
            type: 'symbol',
            source: 'memories',
            filter: ['!', ['has', 'point_count']],  // Only non-clusters
            layout: {
              'icon-image': [
                'match',
                ['downcase', ['get', 'color']],
                '#e6b3c2', 'sticker-rose-medium',
                '#f2c6a0', 'sticker-peach-medium',
                '#c9d3b8', 'sticker-sage-medium',
                '#d4c4e0', 'sticker-lavender-medium',
                '#f4ede4', 'sticker-cream-medium',
                'sticker-rose-medium'
              ],
              'icon-size': [
                'interpolate', ['linear'], ['zoom'],
                8, 0.6,
                10, 0.75,
                12, 0.9,
                14, 1.1,
                16, 1.3
              ],
              'icon-allow-overlap': true,
              // Show text at all zoom levels
              'text-field': ['get', 'text'],
              'text-font': [availableFont],
              'text-size': [
                'interpolate', ['linear'], ['zoom'],
                8, 6,
                10, 7,
                12, 8,
                14, 10,
                16, 12
              ],
              'text-anchor': 'center',
              'text-justify': 'center',
              'text-max-width': 7,
              'text-line-height': 1.1,
              'text-allow-overlap': true,
              'text-padding': 2,
            },
            paint: {
              'text-color': '#4A3F35',
            }
          });
          logger.debug('✅ Native clustering layers added');

          // ========== DEBUG: TEST MEMORY INDICATOR ==========
          // Shows a colored ring around seeded test memories (user_id starts with 'test_user_')
          // TEMPORARILY DISABLED to debug click issues
          if (false && IS_DEBUG_MODE) {
            // Filter for test memories: user_id exists AND starts with 'test_user_'
            const testMemoryFilter = ['all',
              ['!', ['has', 'point_count']],  // Only non-clusters
              ['has', 'user_id'],  // user_id must exist
              ['!=', ['get', 'user_id'], null],  // user_id must not be null
              ['==', ['slice', ['coalesce', ['get', 'user_id'], ''], 0, 10], 'test_user_']
            ];

            // Circle layer underneath test memories for visual distinction
            map.addLayer({
              id: 'debug-test-memory-ring',
              type: 'circle',
              source: 'memories',
              filter: testMemoryFilter,
              paint: {
                'circle-radius': [
                  'interpolate', ['linear'], ['zoom'],
                  8, 18,
                  10, 22,
                  12, 28,
                  14, 35,
                  16, 45
                ],
                'circle-color': 'transparent',
                'circle-stroke-width': 3,
                'circle-stroke-color': '#FF6B6B',  // Coral red border
                'circle-stroke-opacity': 0.8,
              }
            }, 'memory-notes');  // Insert below memory-notes layer

            // Also add a label for test memories (positioned below memory-notes to not intercept clicks)
            map.addLayer({
              id: 'debug-test-memory-label',
              type: 'symbol',
              source: 'memories',
              filter: testMemoryFilter,
              layout: {
                'text-field': '🧪',
                'text-size': 14,
                'text-anchor': 'bottom-right',
                'text-offset': [2.5, -1.5],
                'text-allow-overlap': true,
                'text-ignore-placement': true,
              }
            }, 'memory-notes');  // Insert below memory-notes to not intercept clicks

            // Add click handlers for debug layers that pass through to memory click logic
            map.on('click', 'debug-test-memory-ring', (e) => {
              // Find the corresponding memory-notes feature at this location
              const features = map.queryRenderedFeatures(e.point, { layers: ['memory-notes'] });
              if (features.length > 0) {
                const feature = features[0];
                const props = feature.properties;
                logger.debug('📍 Clicked memory (via debug ring):', props);
                onLocationClickRef.current?.({
                  id: props.id,
                  lat: feature.geometry.coordinates[1],
                  lng: feature.geometry.coordinates[0],
                  text: props.text,
                  color: props.color,
                  user_id: props.user_id ?? null,
                  isMemory: true,
                });
              }
            });

            map.on('click', 'debug-test-memory-label', (e) => {
              const features = map.queryRenderedFeatures(e.point, { layers: ['memory-notes'] });
              if (features.length > 0) {
                const feature = features[0];
                const props = feature.properties;
                logger.debug('📍 Clicked memory (via debug label):', props);
                onLocationClickRef.current?.({
                  id: props.id,
                  lat: feature.geometry.coordinates[1],
                  lng: feature.geometry.coordinates[0],
                  text: props.text,
                  color: props.color,
                  user_id: props.user_id ?? null,
                  isMemory: true,
                });
              }
            });

            logger.debug('🧪 Debug layer added: test memories have coral border + 🧪 indicator');
          }

          // Breathing animation removed for mobile performance.
          // setLayoutProperty every 100ms forced MapLibre to re-evaluate
          // icon-size expressions for every feature on every tick.

          // Add landmarks
          addLandmarks();

          // Initial fetch
          fetchMemories();

          // Fetch on viewport change
          map.on('moveend', debouncedFetch);

          // Track zoom
          map.on('zoom', () => {
            setCurrentZoom(map.getZoom());
          });

          // Click handler for clusters - zoom to expand
          map.on('click', 'memory-clusters', (e) => {
            const features = map.queryRenderedFeatures(e.point, { layers: ['memory-clusters'] });
            if (!features.length) return;

            const clusterId = features[0].properties.cluster_id;
            const source = map.getSource('memories');

            source.getClusterExpansionZoom(clusterId, (err, zoom) => {
              if (err) return;

              map.easeTo({
                center: features[0].geometry.coordinates,
                zoom: zoom + 0.5,
                duration: 500
              });
            });
          });

          // Click handler for individual notes
          map.on('click', 'memory-notes', (e) => {
            const feature = e.features[0];
            const props = feature.properties;
            logger.debug('📍 Clicked memory:', props);
            onLocationClickRef.current?.({
              id: props.id,
              lat: feature.geometry.coordinates[1],
              lng: feature.geometry.coordinates[0],
              text: props.text,
              color: props.color,
              user_id: props.user_id ?? null,
              isMemory: true,
            });
          });

          // Cursor handlers
          map.on('mouseenter', 'memory-clusters', () => {
            map.getCanvas().style.cursor = 'pointer';
          });
          map.on('mouseleave', 'memory-clusters', () => {
            map.getCanvas().style.cursor = '';
          });
          map.on('mouseenter', 'memory-notes', () => {
            map.getCanvas().style.cursor = 'pointer';
          });
          map.on('mouseleave', 'memory-notes', () => {
            map.getCanvas().style.cursor = '';
          });
        });

        // Map click for new location
        map.on('click', e => {
          const features = map.queryRenderedFeatures(e.point, {
            layers: [
              'memory-clusters',
              'memory-notes',
              'landmark-clusters',
              'landmark-cluster-count',
              'landmark-icons',
              'landmark-bg',
            ],
          });
          if (features.length === 0) {
            const { lat, lng } = e.lngLat;
            onLocationClickRef.current?.({ lat, lng });
            // Fly to location with landmark-style transition
            map.flyTo({
              center: [lng, lat],
              zoom: Math.max(map.getZoom(), 15),
              pitch: 45,
              duration: 1500
            });
          }
        });
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          logger.error('Failed to load map style:', err);
        }
      });

    return () => {
      isMounted = false;
      abortController.abort();
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      if (markersForCleanup) {
        markersForCleanup.newMemory?.remove();
      }
      if (mapForCleanup) {
        mapForCleanup.remove();
        mapRef.current = null;
      }
    };
    // Intentionally run once on mount; initialView/theme from first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update map colors when theme changes
  useEffect(() => {
    if (!mapRef.current || !isLoaded || !theme) return;

    const map = mapRef.current;
    const themeColors = theme.colors || DEFAULT_COLORS;

    // Update background layer
    if (map.getLayer('background')) {
      map.setPaintProperty('background', 'background-color', themeColors.land);
    }

    // Update all relevant layers
    const style = map.getStyle();
    if (style && style.layers) {
      style.layers.forEach(layer => {
        if (!layer.paint) return;

        // Water layers
        if (layer.id.includes('water') && layer.type === 'fill' && layer.paint['fill-color']) {
          try {
            map.setPaintProperty(layer.id, 'fill-color', themeColors.ocean);
          } catch (e) {
            // Layer might not support this property
          }
        }

        // Parks and green areas
        if ((layer.id.includes('park') || layer.id.includes('forest') ||
          layer.id.includes('nature') || layer.id.includes('landcover')) &&
          layer.type === 'fill' && layer.paint['fill-color']) {
          try {
            map.setPaintProperty(layer.id, 'fill-color', themeColors.parks);
          } catch (e) { }
        }

        // Roads
        if (layer.id.includes('road') && layer.type === 'line' && layer.paint['line-color']) {
          try {
            map.setPaintProperty(layer.id, 'line-color', themeColors.roads);
          } catch (e) { }
        }

        // Buildings (2D)
        if (layer.id.includes('building') && layer.type === 'fill' &&
          !layer.id.includes('3d') && layer.paint['fill-color']) {
          try {
            map.setPaintProperty(layer.id, 'fill-color', themeColors.buildings);
          } catch (e) { }
        }

        // Borders
        if (layer.id.includes('border') && layer.type === 'line' && layer.paint['line-color']) {
          try {
            map.setPaintProperty(layer.id, 'line-color', themeColors.borders);
          } catch (e) { }
        }
      });
    }

    // Update 3D buildings
    if (map.getLayer('buildings-3d')) {
      map.setPaintProperty('buildings-3d', 'fill-extrusion-color', [
        'interpolate', ['linear'], ['coalesce', ['get', 'render_height'], 10],
        0, themeColors.buildings, 30, themeColors.gold, 80, themeColors.romantic
      ]);
    }
  }, [theme, isLoaded]);

  // Update landmark source when discovered changes
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    const source = mapRef.current.getSource('landmarks');
    if (source) {
      source.setData(buildLandmarkGeoJSON(discovered));
    }
  }, [isLoaded, discovered, buildLandmarkGeoJSON]);

  // First-time onboarding: show help modal after 2.5 seconds
  useEffect(() => {
    if (hasSeenOnboarding || !isLoaded) return;

    const timer = setTimeout(() => {
      setShowOnboardingTrigger(true);
    }, 2500);

    return () => clearTimeout(timer);
  }, [hasSeenOnboarding, isLoaded]);

  // When onboarding triggers, open the help modal
  useEffect(() => {
    if (showOnboardingTrigger && !hasSeenOnboarding) {
      setShowInfoModal(true);
      localStorage.setItem('hasSeenMapOnboarding', 'true');
      setHasSeenOnboarding(true);
      setShowOnboardingTrigger(false);
    }
  }, [showOnboardingTrigger, hasSeenOnboarding]);

  // Refetch when showMyMemories or user changes
  useEffect(() => {
    if (isLoaded) {
      // Clear bounds cache to force refetch with new filter
      lastFetchRef.current = { bounds: null };
      fetchMemories();
    }
  }, [isLoaded, showMyMemories, user, fetchMemories]);

  // Handle new memory
  useEffect(() => {
    if (!newMemory || !mapRef.current || !isLoaded) return;

    const lat = parseFloat(newMemory.lat);
    const lng = parseFloat(newMemory.lng);
    if (isNaN(lat) || isNaN(lng)) {
      onNewMemoryDisplayed?.();
      return;
    }

    // Clear any previous temporary marker
    markersRef.current.newMemory?.remove();
    markersRef.current.newMemory = null;

    // Optimistically inject the new memory into the map source immediately
    const source = mapRef.current.getSource('memories');
    if (source) {
      const currentData = currentGeoJsonRef.current;
      const newFeature = {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lng, lat] },
        properties: {
          id: newMemory.id,
          text: newMemory.text || '',
          color: newMemory.color || '#E6B3C2',
          created_at: newMemory.created_at || new Date().toISOString(),
        }
      };
      const updatedData = {
        type: 'FeatureCollection',
        features: [...(currentData.features || []), newFeature]
      };
      currentGeoJsonRef.current = updatedData;
      source.setData(updatedData);
      setMemoriesInView(prev => prev + 1);
    }

    // Fly to the new memory location
    const map = mapRef.current;
    map.flyTo({
      center: [lng, lat],
      zoom: Math.max(map.getZoom(), 14),
      pitch: 30,
      duration: 1200
    });

    // After fly completes, force-refetch from server to include the new memory
    const onMoveEnd = () => {
      map.off('moveend', onMoveEnd);
      lastFetchRef.current = { bounds: null };
      fetchMemories();
      onNewMemoryDisplayed?.();
    };
    map.once('moveend', onMoveEnd);
  }, [newMemory, isLoaded, onNewMemoryDisplayed, fetchMemories]);

  // Reset flown ref when no longer viewing a share link
  useEffect(() => {
    if (!flyToSharedMemory) flownSharedMemoryIdRef.current = null;
  }, [flyToSharedMemory]);

  // Fly to shared memory when visiting /m/:memoryId (same transition as landmark click)
  useEffect(() => {
    if (!flyToSharedMemory || !mapRef.current || !isLoaded) return;
    const id = flyToSharedMemory.id;
    if (flownSharedMemoryIdRef.current === id) return;

    flownSharedMemoryIdRef.current = id;
    const lat = parseFloat(flyToSharedMemory.lat);
    const lng = parseFloat(flyToSharedMemory.lng);
    if (isNaN(lat) || isNaN(lng)) return;

    const map = mapRef.current;
    map.flyTo({
      center: [lng, lat],
      zoom: FLY_TO_MEMORY.zoom,
      pitch: FLY_TO_MEMORY.pitch,
      bearing: 0,
      duration: FLY_TO_MEMORY.duration,
      essential: true,
    });

    const onMoveEnd = () => {
      map.off('moveend', onMoveEnd);
      lastFetchRef.current = { bounds: null };
      fetchMemories();
      onSharedMemoryFlyComplete?.();
    };
    map.once('moveend', onMoveEnd);
  }, [flyToSharedMemory, isLoaded, onSharedMemoryFlyComplete, fetchMemories]);

  // Rotating phrase above state indicator (empty 😱 or discovered 😍 only, not 🔍 zoom-in)
  const isEmptyOrDiscovered =
    currentZoom >= MIN_ZOOM_FOR_DATA || (showMyMemories && user?.uid) || IS_DEBUG_MODE;
  const phraseKey = memoriesInView === 0 ? 1 : 0; // PHRASES[1] = empty, PHRASES[0] = discovered
  const phraseList = memoriesInView === 0 ? PHRASES[1] : PHRASES[0];

  useEffect(() => {
    if (!isEmptyOrDiscovered || !phraseList?.length) {
      setStatePhrase(null);
      return;
    }
    const pick = () => setStatePhrase(phraseList[Math.floor(Math.random() * phraseList.length)]);
    pick();
    const id = setInterval(pick, 6000);
    return () => clearInterval(id);
  }, [isEmptyOrDiscovered, phraseKey, phraseList]);

  return (
    <div className="map-wrapper" style={{ backgroundColor: colors.ocean }}>
      <div ref={containerRef} className="map-container" />

      {!isLoaded && (
        <div className="map-loading">
          <div className="generating-animation">
            <div className="generating-globe">🌍</div>
            <div className="generating-sparkles">✨</div>
          </div>
        </div>
      )}


      {/* My Memories Mode banner - shows when viewing own memories globally */}
      {showMyMemories && user?.uid && !(selectedLocation?.isLandmark && showLocationCard) && (
        <div className="map-message my-memories-mode">
          <span className="map-message-icon">✨</span>
          <span className="map-message-text">Your memories across the world</span>
        </div>
      )}

      {/* Bottom-left state indicator - changes based on zoom and memories */}
      {isLoaded && !(selectedLocation?.isLandmark && showLocationCard) && (
        <div className="map-state-indicator-wrapper">
          {statePhrase && isEmptyOrDiscovered && (
            <div className="map-state-phrase">
              {statePhrase}
            </div>
          )}
          <div className="map-state-indicator">
            {currentZoom < MIN_ZOOM_FOR_DATA && !(showMyMemories && user?.uid) && !IS_DEBUG_MODE ? (
              // Zoomed out - prompt to zoom in (not in debug mode)
              <span className="state-icon zoom-in" title="Zoom in to discover">🔍</span>
            ) : memoriesInView === 0 ? (
              // Zoomed in but no memories
              <span className="state-icon empty" title="No memories here">😱</span>
            ) : (
              // Memories discovered!
              <span className="state-icon discovered" title={`${memoriesInView} memories`}>😍</span>
            )}
          </div>
        </div>
      )}

      {/* Help button - top right */}
      <button
        className="help-button"
        onClick={() => setShowInfoModal(true)}
        aria-label="Help"
      >
        ?
      </button>

      {/* Help modal */}
      {showInfoModal && (
        <div className="info-modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="info-modal-header">
              <h2 className="info-modal-title">
                <img src="/logo.png" alt="" className="info-modal-logo" />
                Velvynote
              </h2>
              <button
                className="info-modal-close"
                onClick={() => setShowInfoModal(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <p className="info-modal-description">
              Velvynote is an interactive memory map where you can leave post-it note
              memories at any location in the world. Share your favorite spots,
              commemorate special moments, or discover what others have left behind.
            </p>

            <div className="info-modal-tips">
              <div className="info-tip">
                <span className="info-tip-icon">🔍</span>
                <span className="info-tip-text">Zoom in to discover memories left by others</span>
              </div>
              <div className="info-tip">
                <span className="info-tip-icon">👆</span>
                <span className="info-tip-text">Tap anywhere on the map to select a location</span>
              </div>
              <div className="info-tip">
                <span className="info-tip-icon">✨</span>
                <span className="info-tip-text">Tap the sparkle button to leave your memory</span>
              </div>
              <div className="info-tip">
                <span className="info-tip-icon">💌</span>
                <span className="info-tip-text"><strong>Letters:</strong> Tap someone else&apos;s memory → see their profile → &quot;Send a message&quot; to start. Replies appear in Letters (menu). Free daily messages + Buy Gems for more.</span>
              </div>
            </div>

            <div className="info-modal-divider" />

            <div className="info-modal-attribution">
              <span className="info-modal-attribution-label">Map</span>
              <a
                href="https://www.openstreetmap.org/copyright"
                target="_blank"
                rel="noopener noreferrer"
                className="info-modal-attribution-link"
              >
                © OpenStreetMap contributors
              </a>
              <span className="info-modal-attribution-sep"> · </span>
              <a
                href="https://openfreemap.org"
                target="_blank"
                rel="noopener noreferrer"
                className="info-modal-attribution-link"
              >
                OpenFreeMap
              </a>
            </div>

            <div className="info-modal-links">
              <a
                href="/terms"
                className="info-modal-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="info-modal-link-icon">📜</span>
                Terms of Service
              </a>
              <a
                href="/privacy"
                className="info-modal-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="info-modal-link-icon">🔒</span>
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
